import { useCallback, useMemo, useState } from "react";

import { addJWTInConfig } from "../../components/apiHooks/helpers";
import useApiRequestPost from "../../components/apiHooks/useApiRequestPost";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { usePaymentContext } from "../../contexts/PaymentContext/PaymentContext";
import { ILinkpayPaymentRequest, ILinkpayPaymentResponse } from "./types";

const HOST: string = process.env.CLOUD_RUN_GATEWAY_HOST ?? "";
const END_POINT: string = process.env.API_LINKPAY_PAYMENT ?? "";
const PAYMENT_TIMEOUT_MS = 60000;

const url: string = `${HOST}${END_POINT}`;

const useSubmitLinkpayPayment = () => {
	const { state: paymentReference } = usePaymentContext((state) => state.paymentReference);
	const { state: user } = useConfigContext((state) => state.user);
	const [localError, setLocalError] = useState<any>(null);
	const [localLoading, setLocalLoading] = useState(false);

	const requestPayload = useMemo(
		() => ({
			url,
			triggerOnMount: false,
			axiosConfig: addJWTInConfig(
				{ timeout: PAYMENT_TIMEOUT_MS },
				user?.session_id?.toString() ?? ""
			),
			swrConfig: {
				revalidateOnFocus: false,
				revalidateOnReconnect: false,
				revalidateIfStale: false,
				shouldRetryOnError: false,
				errorRetryInterval: 0,
				errorRetryCount: 0,
			},
		}),
		[]
	);

	const {
		status,
		data: response,
		isLoading: swrLoading,
		error: swrError,
		postData,
	} = useApiRequestPost<ILinkpayPaymentResponse>(requestPayload);

	const submitLinkpayPayment = useCallback(
		async (token: string, uid: string) => {
			const body: ILinkpayPaymentRequest = {
				uid: String(uid),
				token,
				payment_reference: paymentReference || "",
			};

			// Reset error state before new request
			setLocalError(null);
			setLocalLoading(true);

			try {
				const result = await postData(body);

				setLocalLoading(false);

				return result;
			} catch (err) {
				console.error("Linkpay payment submission error:", err);
				setLocalError(err);
				setLocalLoading(false);
				throw err;
			}
		},
		[postData, user?.session_id, paymentReference]
	);

	return {
		status,
		submitLinkpayPayment,
		response,
		isLoading: localLoading || swrLoading,
		error: localError || swrError,
	};
};

export default useSubmitLinkpayPayment;
